import { ValidatorFn } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import type { SourceField } from '../../../../features/admin/modules/modules.model';
import { ibanValidator, normalizeIban } from '../../../validators/iban';
import { phoneValidator } from '../../../validators/phone';

/** Format checks an admin can switch on for a plain `input` field (form builder "Validatie"). */
const INPUT_VALIDATORS: Record<Exclude<SourceField['inputValidation'], '' | undefined>, ValidatorFn> = {
  iban: ibanValidator,
  phone: phoneValidator,
};
const ALL_INPUT_VALIDATORS: unknown[] = Object.values(INPUT_VALIDATORS);

/** Formly's `props.blur`: called as `(field, event)` from the input's blur event. */
type BlurHook = NonNullable<NonNullable<FormlyFieldConfig['props']>['blur']>;

/**
 * Blur hooks this extension installed, mapped to the hook that was on the field before (if any).
 * Lets a rebuild recognise its own wrapper instead of stacking a second one, and give the original
 * hook back when the option is switched off.
 */
const IBAN_BLUR_HOOKS = new WeakMap<BlurHook, BlurHook | undefined>();

/**
 * Rewrites what the user typed into the one stored form (compact, capitals), see normalizeIban.
 *
 * Only a DIRTY control is touched. A pristine value is stored data the user never edited — an
 * older entry in the format it was saved in — and leaving such values alone is deliberate: a mere
 * focus + blur must not change data. `setValue` leaves the dirty state as it is; the user's own
 * typing already made the control dirty. Normally the parser (`syncIbanParser`) has already done
 * this on the way in and there is nothing left to rewrite here.
 */
function normalizeTypedIban(field: FormlyFieldConfig): void {
  const control = field.formControl;
  if (!control?.dirty) {
    return;
  }
  const value: unknown = control.value;
  // Strings only: anything else is not something the user typed, and normalizeIban would read it as ''.
  if (typeof value !== 'string') {
    return;
  }
  const normalized = normalizeIban(value);
  if (normalized !== value) {
    control.setValue(normalized);
  }
}

type Parser = NonNullable<FormlyFieldConfig['parsers']>[number];

/** Every parser this extension ever made, so a copy of one (a cloned field config) is recognised. */
const IBAN_PARSERS = new WeakSet<Parser>();
/** The parser made for exactly this field config; a copy carried over from another config is not it. */
const IBAN_PARSER_OF = new WeakMap<FormlyFieldConfig, Parser>();

/**
 * Installs (or removes) the parser that keeps the control in the stored form WHILE the user types.
 *
 * The blur hook alone left a hole: a save with the cursor still in the field (Ctrl+S, Ctrl+Enter,
 * the modal's Enter) reads the model, nothing blurs the field first, and the model held the typed
 * form. Formly runs `parsers` on every value change on the way from the control to the model and
 * writes the parsed value back into the control (ngx-formly-core, `parsers` in the valueChanges
 * pipe), so with this parser neither the control nor the model ever holds spaces or small letters
 * once the user has typed. The blur hook stays as the net for a dirty value written with
 * `emitEvent: false`, which never reaches the parser.
 *
 * Formly types a parser as `(value) => value`; the field it is bound to is captured instead of
 * read from a second argument. Pristine stays pristine: hydration of a stored legacy value passes
 * through unchanged, as on blur.
 */
function syncIbanParser(field: FormlyFieldConfig, enabled: boolean): void {
  const current = field.parsers ?? [];
  const own = IBAN_PARSER_OF.get(field);
  if (enabled && own && current.includes(own)) {
    return;
  }
  const others = current.filter(parser => !IBAN_PARSERS.has(parser));
  if (!enabled) {
    if (others.length !== current.length) {
      field.parsers = others;
    }
    IBAN_PARSER_OF.delete(field);
    return;
  }
  const parser: Parser = (value: unknown) =>
    typeof value === 'string' && field.formControl?.dirty ? normalizeIban(value) : value;
  IBAN_PARSERS.add(parser);
  IBAN_PARSER_OF.set(field, parser);
  field.parsers = [...others, parser];
}

/**
 * Installs (or removes) the normalise-on-blur hook for "Validatie: IBAN". Formly calls
 * `props.blur(field, event)` from the input's blur event, so an existing hook is composed, not
 * replaced: it runs after the normalisation and therefore sees the final value.
 */
function syncIbanBlurHook(field: FormlyFieldConfig, enabled: boolean): void {
  const props = field.props;
  if (!props) {
    return;
  }
  const current = props.blur;
  const installed = current !== undefined && IBAN_BLUR_HOOKS.has(current);

  if (enabled) {
    if (installed) {
      return;
    }
    const hook: BlurHook = (f, event) => {
      normalizeTypedIban(f);
      current?.(f, event);
    };
    IBAN_BLUR_HOOKS.set(hook, current);
    props.blur = hook;
  } else if (installed) {
    const original = IBAN_BLUR_HOOKS.get(current);
    if (original) {
      props.blur = original;
    } else {
      delete props.blur;
    }
  }
}

/**
 * Attaches the format check named in `props.inputValidation` to a plain input.
 *
 * Deliberately a prop rather than Formly's own `validators.validation: ['iban']` in the stored
 * form: Formly throws on a validator name it does not know, so a form saved with that shape would
 * break every build that predates the registration. An unknown prop is simply ignored.
 *
 * Replaces rather than appends, so a rebuild after the prop changed leaves exactly the current check.
 *
 * `iban` also normalises what the user types (compact, capitals) — as it is typed, through a
 * parser, and once more on blur — so the entry stores and shows one format whatever was typed,
 * also when the save comes from the keyboard with the cursor still in the field (#22898).
 */
export function inputValidationExtension(field: FormlyFieldConfig): void {
  if (field.type !== 'input') {
    return;
  }
  const key = field.props?.['inputValidation'];
  syncIbanParser(field, key === 'iban');
  syncIbanBlurHook(field, key === 'iban');
  const validator = Object.hasOwn(INPUT_VALIDATORS, key) ? INPUT_VALIDATORS[key] : undefined;
  const current = field.validators?.validation ?? [];
  const others = current.filter(v => !ALL_INPUT_VALIDATORS.includes(v));
  if (!validator && others.length === current.length) {
    return;
  }
  field.validators = { ...field.validators, validation: validator ? [...others, validator] : others };
}
